import { Injectable, NgZone } from '@angular/core';
import { ARBITRARY_CELL, LayerStoreService } from './layer-store.service';
import { MapBoxService } from '../mapbox.service';
import { SelectedCellService } from './selected-cell.service';
import {
  defer,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import type {
  LngLatBoundsLike,
  LngLatLike,
  MapboxGeoJSONFeature,
} from 'mapbox-gl';
import { LayersDataService } from '../../menu/right-menu/layers-menu/layers-data.service';
import { Feature, MultiPolygon, Polygon } from 'geojson';
import { coordEach } from '@turf/meta';
import { MapHttpService } from './map-http.service';
import { PopupService } from './popup.service';
import { JsonResponse } from '../../../shared/api/backend-config';
import { DataFormatter } from './data-formatter';
import { ToastrService } from 'ngx-toastr';
import { catchError, tap } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { PopupPlacementService } from './popup-placement.service';
import { toMultipolygonWkt } from '../../../shared/util/trade-area-geometry';
import {
  ArbitraryCellApiService,
  CellCreateResponse,
} from '../../../shared/api/arbitrary-cell-api.service';

// The analyzed area's look, drawn before ARBITRARY_CELL (the clickable fill)
// exists. Bottom to top.
const AREA_MASK = 'ARBITRARY_CELL_MASK';
const AREA_HALO = 'ARBITRARY_CELL_HALO';
const AREA_OUTLINE = 'ARBITRARY_CELL_OUTLINE';
const AREA_LABEL = 'ARBITRARY_CELL_LABEL';
const AREA_SOURCE = 'ARBITRARY_CELL_SHAPE';

declare const mapboxgl: typeof import('mapbox-gl');

const OUTLINE_COLOR = '#695dff';
const WORLD_RING = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

// Marching dashes while the backend is indexing, solid once the data is in.
// Lengths are in line widths.
const DASH = 2;
const GAP = 1.5;
const DASH_STEP = 0.25;
const DASH_FRAME_MS = 50;

/** The [DASH, GAP] pattern shifted along the line by `phase`. */
function dashFrame(phase: number): number[] {
  return phase < DASH
    ? [phase, GAP, DASH - phase]
    : [0, phase - DASH, DASH, GAP - (phase - DASH)];
}

@Injectable({
  providedIn: 'root',
})
export class ArbitraryCellService {
  /** Where the analyzed area was measured from - see `drawOrigin`. */
  private originMarker?: mapboxgl.Marker;

  private dashTimer?: ReturnType<typeof setInterval>;

  constructor(
    private mapboxService: MapBoxService,
    private cellApi: ArbitraryCellApiService,
    private mapHttpService: MapHttpService,
    private selectedCellService: SelectedCellService,
    private layerStore: LayerStoreService,
    private popupService: PopupService,
    private layerDataService: LayersDataService,
    private toast: ToastrService,
    private loadingService: LoadingService,
    private placement: PopupPlacementService,
    private zone: NgZone
  ) {}

  /**
   * Registers `geometry` as an arbitrary cell, indexes it and opens its popup
   * at `origin`, the point the area was measured from. Replaces any area
   * analyzed before.
   *
   * Cold, and a single chain on purpose: unsubscribing at any step abandons
   * the whole analysis, so a second request can never be overtaken by a slow
   * first one opening its popup late. Errors are rethrown for the caller to
   * word - a 422 from the create step means the area is outside coverage.
   */
  public analyzeArea(
    geometry: Polygon | MultiPolygon,
    origin: LngLatLike,
    name: string
  ): Observable<void> {
    return defer(() => {
      let shown = false;

      this.resetArbitraryCell();
      this.drawArea(geometry, name);
      this.drawOrigin(origin);
      // A frame late: on a phone the card shrinks on this very tick, and the
      // area is fitted below the card it shrinks to.
      const framing = requestAnimationFrame(() => this.frameArea(geometry));

      return this.cellApi.createCell(toMultipolygonWkt(geometry), name).pipe(
        map((res) => res.object.geoId),
        switchMap((geoId) => this.indexArea(geoId).pipe(map(() => geoId))),
        switchMap((geoId) =>
          this.mapHttpService
            .getCellById(geoId, ARBITRARY_CELL)
            .pipe(map((data) => ({ geoId, properties: data.object })))
        ),
        switchMap(({ geoId, properties }) =>
          this.showArbitraryCell(geometry, geoId, properties, origin)
        ),
        tap(() => (shown = true)),
        catchError((err) => {
          // An open card of the area this one was replacing describes
          // something no longer on the map.
          if (
            this.popupService.popup?.isOpen() &&
            this.popupService.currentlySelectedLevel === ARBITRARY_CELL
          ) {
            this.popupService.handleClose();
          }
          return throwError(() => err);
        }),
        // Failed or cancelled alike: the outline and pin belong to an
        // analysis nobody will see finish.
        finalize(() => {
          cancelAnimationFrame(framing);
          if (!shown) this.resetArbitraryCell();
        })
      );
    });
  }

  public resetArbitraryCell(): void {
    this.layerStore.isArbitraryCellUsed = false;
    this.removeLayers();
  }

  public handleArbitraryCellClick(event: mapboxgl.MapLayerMouseEvent): void {
    const coordinates = event.lngLat;
    const feature = event.features![0];

    this.popupService.handlePopup(feature, coordinates);
  }

  public getArbitraryCellFeature(): MapboxGeoJSONFeature {
    const source = this.mapboxService.map.getSource(
      ARBITRARY_CELL
    ) as mapboxgl.GeoJSONSource;
    //@ts-ignore
    return source._data;
  }

  /**
   * Registers a cell for the comparison page. Swallows every failure into
   * null, warning on a 422 (outside the processed region) along the way.
   */
  public requestCreateArbitraryCell(
    wkt: string,
    name: string
  ): Observable<JsonResponse<CellCreateResponse> | null> {
    return this.cellApi.createCell(wkt, name).pipe(
      catchError((error) => {
        if (error.status === 422) {
          this.toast.warning('Please try another region', 'No data here');
        }
        return of(null);
      })
    );
  }

  private indexArea(geoId: string): Observable<void> {
    return defer(() => {
      this.loadingService.isLoadingManual.next(true);

      return this.cellApi.indexCellAsync(geoId, {
        logLabel: 'map trade area',
        onEstimate: (seconds) => this.notifyEstimatedTime(seconds),
      });
    }).pipe(
      // Success, failure and cancellation alike: the spinner is this request's.
      finalize(() => this.loadingService.isLoadingManual.next(false))
    );
  }

  private notifyEstimatedTime(estimatedIndexTimeSeconds: number): void {
    switch (true) {
      case estimatedIndexTimeSeconds < 5:
        return;

      case estimatedIndexTimeSeconds <= 30:
        this.toast.success('Data will be available soon');
        break;

      case estimatedIndexTimeSeconds <= 120:
        this.toast.warning(
          'Processing a large amount of data, this might take a bit longer. Please wait'
        );
        break;

      default:
        const minutes = Math.ceil(estimatedIndexTimeSeconds / 60);
        this.toast.warning(
          `Processing a large amount of data. Estimated time: ${minutes} minute${minutes > 1 ? 's' : ''}`
        );
        break;
    }
  }

  /**
   * Adds the clickable cell layer and opens its popup once the map has drawn
   * it: the popup needs a *rendered* feature (it reads `layer` and `state`),
   * and `addLayer` only produces one on a later frame.
   */
  private showArbitraryCell(
    geometry: Polygon | MultiPolygon,
    geoId: string,
    properties: Feature['properties'],
    origin: LngLatLike
  ): Observable<void> {
    return new Observable<void>((subscriber) => {
      const map = this.mapboxService.map;

      this.createArbitraryCellLayer({
        type: 'Feature',
        id: geoId,
        geometry,
        properties: {
          external_id: geoId,
          ...DataFormatter.formatArbitraryCellProperties(properties),
        },
      });

      this.stopDashes();
      if (map.getLayer(AREA_OUTLINE)) {
        map.setPaintProperty(AREA_OUTLINE, 'line-dasharray', [1, 0]);
      }

      let reframed = false;

      const onIdle = () =>
        this.zone.run(() => {
          const arbitraryCell = map.queryRenderedFeatures(undefined, {
            layers: [ARBITRARY_CELL],
          })[0];

          if (!arbitraryCell) {
            // Indexing can take minutes, long enough to pan away: bring the
            // area back once before giving up. The repaint guarantees another
            // `idle` even when the camera has nowhere to move.
            if (!reframed && this.frameArea(geometry)) {
              reframed = true;
              map.once('idle', onIdle);
              map.triggerRepaint();
              return;
            }
            subscriber.error(new Error('Analyzed area is not on screen'));
            return;
          }

          this.layerStore.isArbitraryCellUsed = true;
          this.selectedCellService.selectCell(arbitraryCell);
          this.popupService.handlePopup(arbitraryCell, origin);
          this.layerDataService.eventDispatcher('click', arbitraryCell);

          subscriber.next();
          subscriber.complete();
        });

      map.once('idle', onIdle);

      return () => map.off('idle', onIdle);
    });
  }

  private createArbitraryCellLayer(feature: Feature): void {
    if (this.mapboxService.map.getLayer(ARBITRARY_CELL)) return;

    // Under the mask, so its tint cannot wash over the outline and label.
    this.mapboxService.map.addLayer(
      {
        id: ARBITRARY_CELL,
        type: 'fill',
        source: {
          type: 'geojson',
          promoteId: 'external_id',
          data: feature,
        },
        paint: {
          'fill-color': OUTLINE_COLOR,
          'fill-opacity': 0.08,
        },
      },
      AREA_MASK
    );
  }

  /**
   * Drawn as soon as the shape is known, so the wait has something to show.
   * Always onto a clean map: `analyzeArea` resets the previous area first.
   *
   * Added on top of every data layer. The mask fades the rest of the map back
   * and the halo lifts the edge off any colour scale, which is what keeps the
   * area readable when only a strip of it shows past the popup.
   */
  private drawArea(geometry: Polygon | MultiPolygon, label: string): void {
    const map = this.mapboxService.map;
    const polygons =
      geometry.type === 'Polygon'
        ? [geometry.coordinates]
        : geometry.coordinates;

    map.addSource(AREA_SOURCE, {
      type: 'geojson',
      data: { type: 'Feature', geometry, properties: {} },
    });
    // The world with the area's outer rings as holes. geojson-vt rewinds rings
    // by position, so the holes' winding does not matter.
    map.addSource(AREA_MASK, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [WORLD_RING, ...polygons.map(([outer]) => outer)],
        },
      },
    });

    map.addLayer({
      id: AREA_MASK,
      type: 'fill',
      source: AREA_MASK,
      paint: { 'fill-color': '#15132e', 'fill-opacity': 0.3 },
    });
    map.addLayer({
      id: AREA_HALO,
      type: 'line',
      source: AREA_SOURCE,
      layout: { 'line-join': 'round' },
      paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 },
    });
    map.addLayer({
      id: AREA_OUTLINE,
      type: 'line',
      source: AREA_SOURCE,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': OUTLINE_COLOR,
        'line-width': 3,
        'line-dasharray': dashFrame(0),
      },
    });
    map.addLayer({
      id: AREA_LABEL,
      type: 'symbol',
      source: AREA_SOURCE,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 300,
        'text-field': label,
        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
        'text-size': 13,
      },
      paint: {
        'text-color': OUTLINE_COLOR,
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    });

    this.animateDashes();
  }

  private animateDashes(): void {
    const map = this.mapboxService.map;
    let phase = 0;

    // Outside Angular: 20 ticks a second would each run change detection.
    this.zone.runOutsideAngular(() => {
      this.dashTimer = setInterval(() => {
        phase = (phase + DASH_STEP) % (DASH + GAP);
        try {
          map.setPaintProperty(
            AREA_OUTLINE,
            'line-dasharray',
            dashFrame(phase)
          );
        } catch {
          // A style switch removed the map mid-analysis.
          this.stopDashes();
        }
      }, DASH_FRAME_MS);
    });
  }

  private stopDashes(): void {
    clearInterval(this.dashTimer);
    this.dashTimer = undefined;
  }

  /**
   * A drive-time area follows the roads, so its centre is rarely the spot it
   * was measured from, and nothing else on the map says where that spot was.
   * In the outline's colour, so it reads as part of the area rather than as
   * the red search pin.
   */
  private drawOrigin(origin: LngLatLike): void {
    this.originMarker ??= new mapboxgl.Marker({ color: OUTLINE_COLOR });
    this.originMarker.setLngLat(origin).addTo(this.mapboxService.map);
  }

  /**
   * Fits the whole area into the band of map the popup leaves free. False
   * when the map is too small to fit it.
   */
  private frameArea(geometry: Polygon | MultiPolygon): boolean {
    const map = this.mapboxService.map;

    let [west, south, east, north] = [180, 90, -180, -90];
    coordEach(geometry, ([lng, lat]) => {
      west = Math.min(west, lng);
      south = Math.min(south, lat);
      east = Math.max(east, lng);
      north = Math.max(north, lat);
    });

    const bounds: LngLatBoundsLike = [
      [west, south],
      [east, north],
    ];

    // `cameraForBounds` and a plain `flyTo` rather than `fitBounds`, for the
    // reason given in SearchGeocoderService.flyToArea: padding handed to a
    // camera move sticks to the transform and skews every later getCenter().
    const padding = this.placement.getBoundsPadding(map);
    const card = this.placement.getMobileCardPadding(map);
    const camera = map.cameraForBounds(bounds, {
      padding: {
        ...padding,
        top: padding.top + card.top,
        bottom: padding.bottom + card.bottom,
      },
    });
    if (!camera) return false;

    this.zone.runOutsideAngular(() =>
      map.flyTo({ center: camera.center, zoom: camera.zoom })
    );
    return true;
  }

  private removeLayers(): void {
    this.stopDashes();
    this.originMarker?.remove();

    const map = this.mapboxService.map;
    if (!map) return;

    const layers = [
      ARBITRARY_CELL,
      AREA_MASK,
      AREA_HALO,
      AREA_OUTLINE,
      AREA_LABEL,
    ];
    for (const id of layers) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    for (const id of [ARBITRARY_CELL, AREA_MASK, AREA_SOURCE]) {
      if (map.getSource(id)) map.removeSource(id);
    }
  }
}
